import { formatCompact } from './TrendChart';
import { SEQ_RAMP } from './viz';

/**
 * The shared ordinal ramp — legal here because funnel stages ARE ordered. The same ramp on
 * nominal categories (products, countries) would be an anti-pattern: it double-encodes bar length
 * as hue and burns the only free channel on information the bar already shows.
 *
 * Points at `viz.ts` rather than holding its own copy, so the funnel and the circular charts
 * cannot end up encoding "more" with two different blues on the same screen.
 */
const STAGE_RAMP = SEQ_RAMP;

export interface FunnelStage {
  label: string;
  value: number;
  /** Which provider supplied this stage — a funnel spanning three sources must say so. */
  source?: string;
  /** Shown when a stage is an approximation rather than a direct measurement. */
  note?: string;
}

/**
 * Conversion funnel with per-stage drop-off.
 *
 * The drop-off is the point, not the totals: knowing 900 of 1000 sessions never scrolled to the
 * form is actionable in a way that "10 leads" is not.
 *
 * Stages here come from THREE different providers, and that is labelled rather than hidden. GA4
 * and Clarity disagree by construction (Clarity counts bots GA4 filters), so an unlabelled
 * mixed-source funnel invites reading a provider gap as a user drop-off.
 */
export function Funnel({ stages }: { stages: FunnelStage[] }) {
  const usable = stages.filter((s) => Number.isFinite(s.value));

  if (usable.length === 0 || usable[0]!.value === 0) {
    return (
      <div className="empty-state t-body">
        Not enough data to build a funnel yet.
        <div className="t-label subtle" style={{ marginTop: 4 }}>
          Needs sessions at the top of the funnel; leads come from your own backend.
        </div>
      </div>
    );
  }

  const top = usable[0]!.value;

  return (
    <div className="funnel">
      {usable.map((stage, i) => {
        const previous = i === 0 ? stage.value : usable[i - 1]!.value;
        const dropPct = previous > 0 ? ((previous - stage.value) / previous) * 100 : 0;
        const widthPct = Math.max(2, (stage.value / top) * 100);
        // Only label inside the bar when it plausibly fits; otherwise it sits outside. A clipped
        // label is worse than one placed elsewhere.
        const labelFits = widthPct > 30;

        return (
          <div className="funnel-stage" key={stage.label}>
            <div className="funnel-head">
              <span className="t-label">{stage.label}</span>
              <span className="t-label muted">
                {formatCompact(stage.value)}
                <span className="subtle"> · {((stage.value / top) * 100).toFixed(1)}% of top</span>
              </span>
            </div>

            <div className="funnel-track">
              <div
                className="funnel-bar"
                style={{ width: `${widthPct}%`, background: STAGE_RAMP[Math.min(i, STAGE_RAMP.length - 1)] }}
                title={`${stage.label}: ${stage.value.toLocaleString('en-US')}`}
              />
              {labelFits && (
                <span className="funnel-inlabel">{formatCompact(stage.value)}</span>
              )}
            </div>

            <div className="funnel-meta t-caption">
              {stage.source && <span className="chip chip-src">{stage.source}</span>}
              {stage.note && <span className="subtle">{stage.note}</span>}
              {i > 0 && dropPct > 0 && (
                <span className="funnel-drop">−{dropPct.toFixed(1)}% from previous</span>
              )}
            </div>
          </div>
        );
      })}

      {/* Table-view twin: every value readable without relying on colour or hover. */}
      <details className="funnel-table">
        <summary className="t-label">Table view</summary>
        <table className="table">
          <thead>
            <tr><th>Stage</th><th className="num">Value</th><th className="num">Drop-off</th><th>Source</th></tr>
          </thead>
          <tbody>
            {usable.map((s, i) => {
              const prev = i === 0 ? s.value : usable[i - 1]!.value;
              return (
                <tr key={s.label}>
                  <td>{s.label}</td>
                  <td className="num">{s.value.toLocaleString('en-US')}</td>
                  <td className="num">
                    {i === 0 ? '—' : `${(((prev - s.value) / prev) * 100).toFixed(1)}%`}
                  </td>
                  <td className="muted">{s.source ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </details>
    </div>
  );
}
